import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Card, Button, Space, Tag, Table, Modal, Popconfirm, Form, Input, Select, DatePicker, Spin, Alert, Checkbox, Descriptions, Switch, message,
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined, FolderOutlined, CheckCircleFilled, DownOutlined, RightOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { selectFilter } from '../utils/selectFilter';
import LinksFormList from '../components/LinksFormList';
import {
  PROJECT_STATUSES, PROJECT_STATUS_COLOR, TASK_STATUSES, TASK_STATUS_COLOR, PRIORITIES, PRIORITY_COLOR,
  useProjectOptions, LinksView, MembersFormList, MembersView, membersToForm, linksToForm, linksFromForm,
  TaskDrawer, campaignOptions, userLabel, typeOptions,
  taskColumns, taskDeleteColumn, hasOpenTasks, archiveBlocked, ProjectEditModal, AddTaskModal, MassAddTaskModal, projectPath,
} from '../components/ProjectParts';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// A folder (code: project): its details, members and links (members), its
// projects (code: folders) and their tasks (as far as this user sees them),
// and comments. Managers edit the folder and its projects; anyone who sees a
// project adds tasks to it. Visible words use Folder / Project (see
// ProjectParts); variable names keep the code's.

export default function ProjectDetailPage() {
  const { id } = useParams();
  const pid = Number(id);
  const navigate = useNavigate();
  const options = useProjectOptions();
  const [p, setP] = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(p?.Name ? `Folder: ${p.Name}` : null);
  // The Back chip (Layout): where you came from, else Projects.
  useBackFallback('/projects', 'Projects');
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(false);
  const [folderModal, setFolderModal] = useState(null);     // null | { folder }
  const [taskModal, setTaskModal] = useState(null);         // null | { folderId }
  const [massFolder, setMassFolder] = useState(null);       // the Project (code: folder) Mass Add is for
  const [taskId, setTaskId] = useState(null);
  // Folders opened or closed by hand, against their default (closed when
  // every task is Done, else open).
  const [flipped, setFlipped] = useState(() => new Set());
  const flip = (id) => setFlipped((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [saving, setSaving] = useState(false);
  const [pForm] = Form.useForm();

  const load = useCallback(() => {
    api.get(`/api/projects/${pid}`)
      .then((r) => { setP(r.data); setError(null); })
      .catch((err) => setError(err.response?.status === 404 ? 'Folder not found.' : 'Failed to load the folder.'));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  // ?project=<id> (from the Projects page's Projects view): open that
  // project, even if archived or all done, and scroll to it once loaded.
  const [searchParams] = useSearchParams();
  const focusFolder = Number(searchParams.get('project')) || null;
  const focusedRef = React.useRef(null);
  useEffect(() => {
    if (!p || !focusFolder || focusedRef.current === focusFolder) return;
    const f = p.Folders.find((x) => x.ID === focusFolder);
    if (!f) return;
    focusedRef.current = focusFolder;
    if (f.IsArchived) setShowArchived(true);
    const tasks = f.Tasks || [];
    if (tasks.length > 0 && tasks.every((t) => t.Status === 'Done')) {
      setFlipped((cur) => new Set(cur).add(f.ID));
    }
    setTimeout(() => document.getElementById(`folder-${f.ID}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [p, focusFolder]);

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!p) return <Spin />;
  const full = p.MyAccess === 'Manager' || p.MyAccess === 'Member';

  const run = async (fn, okMsg) => {
    setSaving(true);
    try { await fn(); if (okMsg) message.success(okMsg); load(); return true; }
    catch (err) { message.error(err.response?.data?.error || 'Save failed.'); return false; }
    finally { setSaving(false); }
  };

  const hasOpen = hasOpenTasks;

  // -- project -----------------------------------------------------------------
  const openEdit = () => {
    pForm.setFieldsValue({
      name: p.Name, description: p.Description || '', status: p.Status, owner_user_id: p.OwnerUserID, type_id: p.TypeID ?? undefined,
      dates: [p.StartDate ? dayjs(p.StartDate) : null, p.EndDate ? dayjs(p.EndDate) : null],
      campaign_ids: p.Campaigns.map((c) => c.ID), members: membersToForm(p.Members), links: linksToForm(p.Links),
    });
    setEditing(true);
  };
  const saveProject = async () => {
    let v;
    try { v = await pForm.validateFields(); } catch { return; }
    if (v.status === 'Archived' && p.Status !== 'Archived' && hasOpen(p.Folders)) { archiveBlocked(); return; }
    const ok = await run(() => api.put(`/api/projects/${pid}`, {
      name: v.name, description: v.description || '', status: v.status,
      start_date: v.dates?.[0]?.format('YYYY-MM-DD') || null, end_date: v.dates?.[1]?.format('YYYY-MM-DD') || null,
      campaign_ids: v.campaign_ids || [], members: v.members || [], links: linksFromForm(v.links),
      type_id: v.type_id ?? null,
      // Only when changed: an owner since made inactive can stay until replaced.
      ...(v.owner_user_id !== p.OwnerUserID ? { owner_user_id: v.owner_user_id } : {}),
    }), 'Folder saved.');
    if (ok) setEditing(false);
  };

  // -- projects (code: folders) and tasks: shared modals --------------------------
  const openFolder = (folder) => setFolderModal({ folder });
  const openNewTask = (folderId) => setTaskModal({ folderId });
  // The task's campaign scope: its project's campaigns, else its folder's.
  const scopeOf = (folderId) => {
    const own = p.Folders.find((x) => x.ID === folderId)?.Campaigns || [];
    return (own.length ? own : p.Campaigns).map((c) => c.ID);
  };
  const taskCols = p.CanManage ? [taskDeleteColumn(() => true, load), ...taskColumns()] : taskColumns();

  const folders = p.Folders.filter((f) => showArchived || !f.IsArchived);

  return (
    <div>
      <Space align="center" style={{ marginBottom: 12 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>{p.Name}</Typography.Title>
        <Tag color={PROJECT_STATUS_COLOR[p.Status]}>{p.Status}</Tag>
      </Space>

      {full && (
        <Card size="small" title={<Space>Folder{p.TypeName && <Tag color="geekblue">{p.TypeName}</Tag>}</Space>} style={{ marginBottom: 16 }}
          extra={p.CanManage && <Button size="small" icon={<EditOutlined />} onClick={openEdit}>Edit</Button>}>
          <Descriptions size="small" bordered column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="Owner">{p.OwnerName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Dates">{p.StartDate || p.EndDate ? `${p.StartDate || '…'} – ${p.EndDate || '…'}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Campaigns" span={2}>
              {p.Campaigns.length ? p.Campaigns.map((c) => <Tag key={c.ID}>{c.Name}</Tag>) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {p.Description ? <div style={{ whiteSpace: 'pre-wrap' }}>{p.Description}</div> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Members" span={2}><MembersView members={p.Members} /></Descriptions.Item>
            <Descriptions.Item label="Links" span={2}>{p.Links.length ? <LinksView links={p.Links} /> : '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}
      {!full && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message={p.MyAccess === 'Folder' ? 'You see the projects you are a member of.' : 'You see the tasks assigned to you.'} />
      )}

      <Space style={{ marginBottom: 8 }} wrap>
        {p.CanManage && <Button type="primary" icon={<PlusOutlined />} onClick={() => openFolder(null)}>Add Project</Button>}
        {p.Folders.some((f) => f.IsArchived) && (
          <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}>Show archived projects</Checkbox>
        )}
      </Space>

      {!folders.length && <Typography.Paragraph type="secondary">No projects yet.</Typography.Paragraph>}
      {folders.map((f) => {
        // Open = tasks not yet Done. A folder whose tasks are all Done shows a
        // green check and starts closed; the header opens and closes it.
        const tasks = f.Tasks || [];
        const open = tasks.filter((t) => t.Status !== 'Done').length;
        const allDone = tasks.length > 0 && open === 0;
        const closed = allDone !== flipped.has(f.ID);
        return (
        <Card key={f.ID} id={`folder-${f.ID}`} size="small" className="project-folder-card" style={{ marginBottom: 12 }}
          styles={{ body: closed ? { display: 'none' } : undefined }}
          title={(
            <Space style={{ cursor: 'pointer' }} onClick={() => flip(f.ID)}>
              {closed ? <RightOutlined style={{ fontSize: 11 }} /> : <DownOutlined style={{ fontSize: 11 }} />}
              {allDone ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : <FolderOutlined />}
              {f.Name}
              <Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>
                {allDone ? '(all tasks done)' : `(${open} open task${open === 1 ? '' : 's'})`}
              </Typography.Text>
              {f.TypeName && <Tag color="geekblue" style={{ fontWeight: 'normal' }}>{f.TypeName}</Tag>}
              {(f.StartDate || f.EndDate) && (
                <Typography.Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>
                  {f.StartDate || '…'} – {f.EndDate || '…'}
                </Typography.Text>
              )}
              {f.IsArchived && <Tag>Archived</Tag>}
            </Space>
          )}
          extra={(
            <Space>
              <Button size="small" onClick={() => navigate(projectPath(pid, f.ID))}>Open</Button>
              {f.CanAddTasks && <Button size="small" onClick={() => setMassFolder(f.ID)}>Mass Add Tasks</Button>}
              {f.CanAddTasks && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openNewTask(f.ID)}>Add Task</Button>}
              {p.CanManage && <Button size="small" icon={<EditOutlined />} onClick={() => openFolder(f)}>Edit</Button>}
              {/* A project with no tasks can be deleted. */}
              {p.CanManage && !tasks.length && (
                <Popconfirm title="Delete this project?" description="It has no tasks." okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => api.delete(`/api/project-folders/${f.ID}`)
                    .then(() => { message.success('Project deleted.'); load(); })
                    .catch((err) => message.error(err.response?.data?.error || 'Delete failed.'))}>
                  <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              )}
            </Space>
          )}>
          {f.Description && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{f.Description}</div>}
          {f.Campaigns?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Campaigns: </Typography.Text>
              {f.Campaigns.map((c) => <Tag key={c.ID}>{c.Name}</Tag>)}
            </div>
          )}
          {f.Links.length > 0 && <div style={{ marginBottom: 8 }}><LinksView links={f.Links} /></div>}
          {p.CanManage && f.Members.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Project members: </Typography.Text>
              <MembersView members={f.Members} />
            </div>
          )}
          <Table rowKey="ID" size="small" pagination={false} dataSource={f.Tasks} columns={taskCols}
            locale={{ emptyText: 'No tasks.' }}
            onRow={(t) => ({ onClick: () => setTaskId(t.ID), style: { cursor: 'pointer' } })} />
        </Card>
        );
      })}

      {/* No comments on a Folder: it only groups Projects, and each Project
          has its own comments on its page. */}

      <TaskDrawer open={!!taskId} taskId={taskId} onClose={() => setTaskId(null)} onChanged={load} options={options} />

      <Modal maskClosable={false} title="Edit Folder" open={editing} onOk={saveProject} onCancel={() => setEditing(false)}
        confirmLoading={saving} okText="Save" width={720} destroyOnClose>
        <Form form={pForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}><Input maxLength={200} /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} /></Form.Item>
          <Space wrap size={12}>
            {/* Owner: always a Manager. Change it when the owner leaves; an
                owner who is no longer active shows until replaced. */}
            <Form.Item name="owner_user_id" label="Owner" rules={[{ required: true, message: 'Pick an owner' }]}>
              <Select showSearch filterOption={selectFilter} style={{ width: 240 }}
                options={[
                  ...(options.users.some((u) => u.ID === p.OwnerUserID) ? []
                    : [{ value: p.OwnerUserID, label: `${p.OwnerName || `#${p.OwnerUserID}`} (inactive)`, disabled: true }]),
                  ...options.users.map((u) => ({ value: u.ID, label: userLabel(u) })),
                ]} />
            </Form.Item>
            <Form.Item name="status" label="Status">
              <Select style={{ width: 140 }} options={PROJECT_STATUSES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
            <Form.Item name="dates" label="Dates"><DatePicker.RangePicker allowEmpty={[true, true]} /></Form.Item>
            <Form.Item name="type_id" label="Type">
              <Select allowClear showSearch filterOption={selectFilter} style={{ width: 220 }} placeholder="No type"
                options={typeOptions(options, p.TypeID, p.TypeName)} />
            </Form.Item>
          </Space>
          <Form.Item name="campaign_ids" label="Campaigns">
            <Select mode="multiple" showSearch filterOption={selectFilter} options={campaignOptions(options, p.Campaigns)} />
          </Form.Item>
          <Form.Item label="Members"><MembersFormList name="members" options={options} /></Form.Item>
          <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
        </Form>
      </Modal>

      <ProjectEditModal open={!!folderModal} folderId={pid} project={folderModal?.folder} options={options}
        onClose={() => setFolderModal(null)} onSaved={load} />
      <MassAddTaskModal open={!!massFolder} folderId={pid} projectId={massFolder} options={options}
        onClose={() => setMassFolder(null)} onSaved={load} />
      <AddTaskModal open={!!taskModal} folderId={pid} projectId={taskModal?.folderId} campaignIds={taskModal ? scopeOf(taskModal.folderId) : []}
        options={options} onClose={() => setTaskModal(null)} onSaved={load} />
    </div>
  );
}
